import {Observable, Subject} from "rxjs";
import {singleton} from "tsyringe";
import {isRteDraftForeign} from "./rte-draft-meta.helper";
import {peekWhiteboardDraft} from "./whiteboard-draft-store.helper";

/**
 * "Does this KnowledgeObject have an unsaved local draft" check, spanning
 * BOTH draft mechanisms currently in the app. It only ever READS localStorage
 * — it runs in the sidebar's render path (once per row, every render), so it
 * must never write or remove anything:
 * - `@o2d/rich-text-editor`'s own `DraftStore`
 *   (`packages/rich-text-editor/src/controllers/draft-store.ts`), key
 *   format `o2d.rte-draft.<uid>` — that package owns writing/reading it,
 *   this is a read-only peek at its localStorage convention.
 * - `whiteboard-draft-store.helper.ts` (this repo), key format
 *   `o2d.whiteboard-draft.<uid>.<userUid>`.
 *
 * "Has a draft" means a draft the given user could actually resume
 * (review 2026-09-19, F7/F13): the whiteboard side asks the store's
 * non-destructive `peekWhiteboardDraft` — a legacy/corrupt/foreign entry
 * never lights the indicator, which used to stay on forever for a draft
 * nothing would ever restore (removing such entries is `loadWhiteboardDraft`'s
 * job at load time, not this function's) — and an RTE draft whose side-car
 * meta names another user is ignored. An RTE draft without meta (owner
 * unknown) still counts.
 *
 * Used by `knowledge-sidebar-list.component.ts` to decide whether a row
 * gets the italic + superscript-asterisk "unsaved" treatment (Anke,
 * 2026-09-16), mirroring the existing per-object-toolbar indicator
 * (`knowledge-rte.component.ts`'s `--unsaved` class /
 * `knowledge-whiteboard.component.ts`'s `.fileNameUnsaved`).
 */
const RTE_DRAFT_KEY_PREFIX = "o2d.rte-draft.";

export function hasKnowledgeDraft(uid: string, userUid: string): boolean {
    try {
        const hasOwnRteDraft = localStorage.getItem(`${RTE_DRAFT_KEY_PREFIX}${uid}`) !== null
            && !isRteDraftForeign(uid, userUid);
        return hasOwnRteDraft || peekWhiteboardDraft(uid, userUid);
    } catch {
        return false;
    }
}

/**
 * Same-tab-only notification that SOME uid's draft state may have changed
 * (written, cleared, or restored). A plain `localStorage` `storage` event
 * only fires in OTHER tabs, never the tab that made the write — so the
 * sidebar list (same tab as the open editor) needs an explicit signal to
 * re-check `hasKnowledgeDraft()` and re-render live instead of only on its
 * next unrelated render. Callers pass the affected uid for a future,
 * more targeted subscriber; today's only subscriber
 * (`knowledge-sidebar-list.component.ts`) just re-renders on any emission.
 */
@singleton()
export class KnowledgeDraftChangeNotifier {
    private readonly _changed = new Subject<string>();
    // Exposed as the bare Subject (no `.asObservable()` wrapper) — the
    // wrapper is a pure defensive-API nicety that several test suites'
    // hand-rolled `rxjs` mocks (a minimal `MockSubject` with no
    // `asObservable` method) do not implement; `Subject` already satisfies
    // `Observable<string>` for every consumer here, which only ever calls
    // `.pipe()`/`.subscribe()`.
    public readonly changed$: Observable<string> = this._changed;

    public notify(uid: string): void {
        this._changed.next(uid);
    }
}
